import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, addDays } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select, 
    Tooltip,     
} from '@mui/material';
import { countValuesByField, countValuesByFields } from 'scenes/charts/utils';
import { 
    countOTPOverall,
    countDepOTPOverallAffectedByArrDelay,
    countOTPByField,  
    countOTPBy2Fields,
    countOTPAffectedByArrDelay,
    countDelayPropagationMetrics,
    countDepOTPByDelayCode,
    countOTPByFieldStackedBarChart,
    calculateOTPByETDRange,
    calculateDelaysTimeByETDRange,
    calculateOTPByWeekday,
    calculateDelaysTimeByWeekday,
    countPaxAffectedByDelay,
    countPaxAffectedByDepOTP,
    countHeatmapByDelayCode,
} from './utils';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { 
    LAYOUT_HEIGHT, 
    DIVERT_RETURN_STATUS, 
    EXCLUDED_FLIGHT_STATUS 
} from 'state/constants';
import { 
    removeItemByValueImmutable, 
    parseUTCDate, 
    filterFlights, 
} from 'state/helpers';
import useAuthCheck from 'hooks/useAuthCheck';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import NumberControl from 'components/NumberControl';
import ChartDialog from 'components/chartNivo/ChartDialog';
import FlightFilterDialog from 'components/FlightFilterDialog';
import PieChart from 'components/chartNivo/PieChart';
import HeatMap from 'components/chartNivo/HeatMap';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import TimeRangeChart from 'components/chartNivo/TimeRangeChart';
import { boxStyles as styles } from 'components/CustomStyles';
import OtpTableau from './OtpTableau';


const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const viewOptions = {
    chart: [
        { value: "otpOverall", label: "Overview" },
        { value: "otpByDate", label: "by Date" },
        { value: "otpByPort", label: "by Port" },
        { value: "otpBySector", label: "by Sector" },
        { value: "otpBySectorType", label: "by Sector Type" },
        { value: "otpByFlightType", label: "by Flight Type" },
        { value: "otpByFlightReg", label: "by Flight Reg" },
        { value: "otpByACType", label: "by AC Type" },
        { value: "otpByCountry", label: "by Country" },
        { value: "otpByFlightNo", label: "by Flight No" },
        { value: "otpByDelayCode", label: "by Delay Code" },
        { value: "otpByETDRange", label: "by Etd Range" },
        { value: "otpByWeekday", label: "by Weekday" },
        { value: "heatmapByDelayCode", label: "Heat Map" },
    ],
    tableau: [
        { value: "otpByDate", label: "by Date" },
        { value: "otpByDateDep", label: "by Date & Port" },
        { value: "otpByDateSector", label: "by Date & Sector" },
        { value: "otpByDateSectorType", label: "by Date & Sector Type" },
        { value: "otpByDateFlightType", label: "by Date & Flight Type" },
        { value: "otpByDateFlightReg", label: "by Date & Flight Reg" },
        { value: "otpByDateACType", label: "by Date & AC Type" },
        { value: "otpByDateCountry", label: "by Date & Country" },
        { value: "otpByDateFlightNo", label: "by Date & Flight No" },

        { value: "otpByDep", label: "by Port" },
        { value: "otpBySector", label: "by Sector" },
        { value: "otpBySectorType", label: "by Sector Type" },
        { value: "otpByFlightType", label: "by Flight Type" },
        
        { value: "otpByFlightReg", label: "by Flight Reg" },
        { value: "otpByFlightRegDep", label: "by Flight Reg & Port" },
        { value: "otpByFlightRegSector", label: "by Flight Reg & Sector" },
        { value: "otpByFlightRegSectorType", label: "by Flight Reg & Sector Type" },
        { value: "otpByFlightRegFlightType", label: "by Flight Reg & Flight Type" },
        { value: "otpByFlightRegCountry", label: "by Flight Reg & Country" },
        
        { value: "otpByACType", label: "by AC Type" },
        { value: "otpByACTypeDep", label: "by AC Type & Port" },
        { value: "otpByACTypeSector", label: "by AC Type & Sector" },
        { value: "otpByACTypeSectorType", label: "by AC Type & Sector Type" },
        { value: "otpByACTypeFlightType", label: "by AC Type & Flight Type" },
        { value: "otpByACTypeCountry", label: "by AC Type & Country" },
        
        { value: "otpByCountry", label: "by Country" },
        
        { value: "otpByFlightNo", label: "by Flight No" },
    ],
};

const OTPAnalytics = () => {
    useAuthCheck();    
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();    
    
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [otpDelayMinutes, setOtpDelayMinutes] = useState(15);
    
    const [delayType, setDelayType] = useState('departure');
    const [displayType, setDisplayType] = useState('chart');
    const [view, setView] = useState('otpOverall');
    const [flights, setFlights] = useState([]); 
    
    const [data, setData] = useState({        
        otpOverallPie: [],
        otpOverallBar: [],
        otpOverallAffectedByPreviousLegPie: [],        
        paxOverallPie: [],
        paxOverallBar: [],

        otpByFlightDate: [],
        multiOTPByFlightDate: [],
        propagationIndexByFlightDate: [],
        otpTimeRangeByFlightDate: [],
        otpByStackedFlightDate: [],
        delayTimeByFlightDate: [],
        flightDateDelayTimeAffectedByPreviousLeg: [],
        paxByFlightDate: [],
        
        otpByPort: [],
        multiOTPByPort: [],
        propagationIndexByPort: [],
        otpByStackedPort: [],
        delayTimeByPort: [],
        depDelayTimeAffectedByPreviousLeg: [],
        paxByPort: [],

        otpBySector: [],
        multiOTPBySector: [],
        propagationIndexBySector: [],
        otpByStackedSector: [],
        delayTimeBySector: [],
        sectorDelayTimeAffectedByPreviousLeg: [],
        paxBySector: [],
        
        otpBySectorType: [],
        multiOTPBySectorType: [],
        propagationIndexBySectorType: [],
        otpByStackedSectorType: [],
        delayTimeBySectorType: [],     
        sectorTypeDelayTimeAffectedByPreviousLeg: [],  
        paxBySectorType: [],       

        otpByFlightType: [],
        multiOTPByFlightType: [],
        propagationIndexByFlightType: [],
        otpByStackedFlightType: [],
        delayTimeByFlightType: [],
        flightTypeDelayTimeAffectedByPreviousLeg: [],  
        paxByFlightType: [],

        otpByFlightReg: [],
        multiOTPByFlightReg: [],
        propagationIndexByFlightReg: [],
        otpByStackedFlightReg: [],
        delayTimeByFlightReg: [],
        flightRegDelayTimeAffectedByPreviousLeg: [],  
        paxByFlightReg: [],

        otpByACType: [],
        multiOTPByACType: [],
        propagationIndexByACType: [],
        otpByStackedACType: [],
        delayTimeByACType: [],       
        acTypeDelayTimeAffectedByPreviousLeg: [],  
        paxByACType: [],       

        otpByCountry: [],
        multiOTPByCountry: [],
        propagationIndexByCountry: [],
        otpByStackedCountry: [],
        delayTimeByCountry: [],       
        countryDelayTimeAffectedByPreviousLeg: [],  
        paxByCountry: [],
        
        otpByFlightNo: [],
        multiOTPByFlightNo: [],
        propagationIndexByFlightNo: [],
        otpByStackedFlightNo: [],
        delayTimeByFlightNo: [],
        flightNoDelayTimeAffectedByPreviousLeg: [],  
        paxByFlightNo: [],

        otpByDelayCode: [],

        otpByETDRange: [],
        delayTimeByETDRange: [],

        otpByWeekday: [],
        delayTimeByWeekday: [],

        heatmapByDelayCode: [],

        tableauOtpByDate: [],
        tableauOtpByDateDep: [], 
        tableauOtpByDateSector: [], 
        tableauOtpByDateSectorType: [], 
        tableauOtpByDateFlightType: [], 
        tableauOtpByDateFlightReg: [], 
        tableauOtpByDateACType: [], 
        tableauOtpByDateCountry: [], 
        tableauOtpByDateFlightNo: [], 

        tableauOtpByDep: [],
        tableauOtpBySector: [],
        tableauOtpBySectorType: [],
        tableauOtpByFlightType: [],
        
        tableauOtpByFlightReg: [], 
        tableauOtpByFlightRegDep: [], 
        tableauOtpByFlightRegSector: [], 
        tableauOtpByFlightRegSectorType: [], 
        tableauOtpByFlightRegFlightType: [], 
        tableauOtpByFlightRegCountry: [], 
        
        tableauOtpByACType: [],
        tableauOtpByACTypeDep: [], 
        tableauOtpByACTypeSector: [], 
        tableauOtpByACTypeSectorType: [], 
        tableauOtpByACTypeFlightType: [], 
        tableauOtpByACTypeCountry: [],
        
        tableauOtpByCountry: [],

        tableauOtpByFlightNo: [], 
    });
    
    const flightsData = useSelector((state) => state.flights); 
        
    useEffect(() => {
        const options = viewOptions[displayType];
        if (!options?.length) return;

        const isValid = options.some(opt => opt.value === view);

        if (!isValid) {
            setView(options[0].value); 
        }
    }, [displayType, view]);

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view)
            ? view
            : options[0]?.value || '';
    }, [view, displayType]);

    useEffect(() => {        
        const handleFlights = async () => {
            try {
                setError(null);           
                setLoading(true); 

                const processedFlights = removeItemByValueImmutable(flightsData, 'flightStatus', EXCLUDED_FLIGHT_STATUS)
                    .filter(flight => 
                        !DIVERT_RETURN_STATUS.includes(flight.flightStatus)
                    );
                setFlights(processedFlights);                
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };        
        handleFlights();      
    }, [flightsData]); 
            
    const filteredFlights = useMemo(() => {
        return filterFlights({ flights, filters, timeMode });
    }, [flights, filters, timeMode]);       
    
    useEffect(() => {        
        const flightDate = timeMode === 'UTC' ? 'flightDate' : 'flightDateLocalBase'; 
               
        const results = {    
            otpOverallPie: countOTPOverall(filteredFlights, delayType, 'pie', otpDelayMinutes),
            otpOverallBar: countOTPOverall(filteredFlights, delayType, 'column', otpDelayMinutes),
            otpOverallAffectedByPreviousLegPie: countDepOTPOverallAffectedByArrDelay(filteredFlights, delayType, 'pie', otpDelayMinutes),
            paxOverallPie: countPaxAffectedByDepOTP(filteredFlights, delayType, 'pie', otpDelayMinutes),
            paxOverallBar: countPaxAffectedByDepOTP(filteredFlights, delayType, 'column', otpDelayMinutes),

            otpByFlightDate: countOTPByField(filteredFlights, flightDate, delayType, 'barlines', otpDelayMinutes),
            multiOTPByFlightDate: countOTPAffectedByArrDelay(filteredFlights, flightDate, delayType, 'barlines', otpDelayMinutes),
            propagationIndexByFlightDate: countDelayPropagationMetrics(filteredFlights, flightDate, otpDelayMinutes),
            otpTimeRangeByFlightDate: countOTPByField(filteredFlights, flightDate, delayType, 'timerange', otpDelayMinutes),
            otpByStackedFlightDate: countOTPByFieldStackedBarChart(filteredFlights, flightDate, delayType, otpDelayMinutes),
            delayTimeByFlightDate: countValuesByField(filteredFlights, delayType === 'departure' ? 'depDelaysTime' : 'arrDelaysTime', flightDate, 'barlines', otpDelayMinutes),
            flightDateDelayTimeAffectedByPreviousLeg: countValuesByFields(filteredFlights, 'depDelaysTime', 'preArrDelaysTime', flightDate, otpDelayMinutes),
            paxByFlightDate: countPaxAffectedByDelay(filteredFlights, flightDate, delayType, 'barlines', otpDelayMinutes),
            
            otpByPort: countOTPByField(filteredFlights, delayType === 'departure' ? 'dep' : 'arr', delayType, 'barlines', otpDelayMinutes),
            multiOTPByPort: countOTPAffectedByArrDelay(filteredFlights, delayType === 'departure' ? 'dep' : 'arr', delayType, 'barlines', otpDelayMinutes),
            propagationIndexByPort: countDelayPropagationMetrics(filteredFlights, 'dep', otpDelayMinutes),
            otpByStackedPort: countOTPByFieldStackedBarChart(filteredFlights, delayType === 'departure' ? 'dep' : 'arr', delayType, otpDelayMinutes),
            delayTimeByPort: countValuesByField(filteredFlights, delayType === 'departure' ? 'depDelaysTime' : 'arrDelaysTime', delayType === 'departure' ? 'dep' : 'arr', 'barlines', otpDelayMinutes),
            depDelayTimeAffectedByPreviousLeg: countValuesByFields(filteredFlights, 'depDelaysTime', 'preArrDelaysTime', 'dep', otpDelayMinutes),
            paxByPort: countPaxAffectedByDelay(filteredFlights, delayType === 'departure' ? 'dep' : 'arr', delayType, 'barlines', otpDelayMinutes),

            otpBySector: countOTPByField(filteredFlights, 'sector', delayType, 'barlines', otpDelayMinutes),
            multiOTPBySector: countOTPAffectedByArrDelay(filteredFlights, 'sector', delayType, 'barlines', otpDelayMinutes),
            propagationIndexBySector: countDelayPropagationMetrics(filteredFlights, 'sector', otpDelayMinutes),
            otpByStackedSector: countOTPByFieldStackedBarChart(filteredFlights, 'sector', delayType, otpDelayMinutes),
            delayTimeBySector: countValuesByField(filteredFlights, delayType === 'departure' ? 'depDelaysTime' : 'arrDelaysTime', 'sector', 'barlines', otpDelayMinutes),
            sectorDelayTimeAffectedByPreviousLeg: countValuesByFields(filteredFlights, 'depDelaysTime', 'preArrDelaysTime', 'sector', otpDelayMinutes),
            paxBySector: countPaxAffectedByDelay(filteredFlights, 'sector', delayType, 'barlines', otpDelayMinutes),

            otpBySectorType: countOTPByField(filteredFlights, 'sectorType', delayType, 'barlines', otpDelayMinutes),
            multiOTPBySectorType: countOTPAffectedByArrDelay(filteredFlights, 'sectorType', delayType, 'barlines', otpDelayMinutes),
            propagationIndexBySectorType: countDelayPropagationMetrics(filteredFlights, 'sectorType', otpDelayMinutes),
            otpByStackedSectorType: countOTPByFieldStackedBarChart(filteredFlights, 'sectorType', delayType, otpDelayMinutes),
            delayTimeBySectorType: countValuesByField(filteredFlights, delayType === 'departure' ? 'depDelaysTime' : 'arrDelaysTime', 'sectorType', 'barlines', otpDelayMinutes),
            sectorTypeDelayTimeAffectedByPreviousLeg: countValuesByFields(filteredFlights, 'depDelaysTime', 'preArrDelaysTime', 'sectorType', otpDelayMinutes),
            paxBySectorType: countPaxAffectedByDelay(filteredFlights, 'sectorType', delayType, 'barlines', otpDelayMinutes),

            otpByFlightType: countOTPByField(filteredFlights, 'flightType', delayType, 'barlines', otpDelayMinutes),
            multiOTPByFlightType: countOTPAffectedByArrDelay(filteredFlights, 'flightType', delayType, 'barlines', otpDelayMinutes),
            propagationIndexByFlightType: countDelayPropagationMetrics(filteredFlights, 'flightType', otpDelayMinutes),
            otpByStackedFlightType: countOTPByFieldStackedBarChart(filteredFlights, 'flightType', delayType, otpDelayMinutes),
            delayTimeByFlightType: countValuesByField(filteredFlights, delayType === 'departure' ? 'depDelaysTime' : 'arrDelaysTime', 'flightType', 'barlines', otpDelayMinutes),
            flightTypeDelayTimeAffectedByPreviousLeg: countValuesByFields(filteredFlights, 'depDelaysTime', 'preArrDelaysTime', 'flightType', otpDelayMinutes),
            paxByFlightType: countPaxAffectedByDelay(filteredFlights, 'flightType', delayType, 'barlines', otpDelayMinutes),

            otpByFlightReg: countOTPByField(filteredFlights, 'flightReg', delayType, 'barlines', otpDelayMinutes),
            multiOTPByFlightReg: countOTPAffectedByArrDelay(filteredFlights, 'flightReg', delayType, 'barlines', otpDelayMinutes),
            propagationIndexByFlightReg: countDelayPropagationMetrics(filteredFlights, 'flightReg', otpDelayMinutes),
            otpByStackedFlightReg: countOTPByFieldStackedBarChart(filteredFlights, 'flightReg', delayType, otpDelayMinutes),
            delayTimeByFlightReg: countValuesByField(filteredFlights, delayType === 'departure' ? 'depDelaysTime' : 'arrDelaysTime', 'flightReg', 'barlines', otpDelayMinutes),
            flightRegDelayTimeAffectedByPreviousLeg: countValuesByFields(filteredFlights, 'depDelaysTime', 'preArrDelaysTime', 'flightReg', otpDelayMinutes),
            paxByFlightReg: countPaxAffectedByDelay(filteredFlights, 'flightReg', delayType, 'barlines', otpDelayMinutes),

            otpByACType: countOTPByField(filteredFlights, 'acType', delayType, 'barlines', otpDelayMinutes),
            multiOTPByACType: countOTPAffectedByArrDelay(filteredFlights, 'acType', delayType, 'barlines', otpDelayMinutes),
            propagationIndexByACType: countDelayPropagationMetrics(filteredFlights, 'acType', otpDelayMinutes),
            otpByStackedACType: countOTPByFieldStackedBarChart(filteredFlights, 'acType', delayType, otpDelayMinutes),
            delayTimeByACType: countValuesByField(filteredFlights, delayType === 'departure' ? 'depDelaysTime' : 'arrDelaysTime', 'acType', 'barlines', otpDelayMinutes),
            acTypeDelayTimeAffectedByPreviousLeg: countValuesByFields(filteredFlights, 'depDelaysTime', 'preArrDelaysTime', 'acType', otpDelayMinutes),
            paxByACType: countPaxAffectedByDelay(filteredFlights, 'acType', delayType, 'barlines', otpDelayMinutes),

            otpByCountry: countOTPByField(filteredFlights, delayType === 'departure' ? 'depCountryName' : 'arrCountryName', delayType, 'barlines', otpDelayMinutes),
            multiOTPByCountry: countOTPAffectedByArrDelay(filteredFlights, delayType === 'departure' ? 'depCountryName' : 'arrCountryName', delayType, 'barlines', otpDelayMinutes),
            propagationIndexByCountry: countDelayPropagationMetrics(filteredFlights, 'depCountryName', otpDelayMinutes),
            otpByStackedCountry: countOTPByFieldStackedBarChart(filteredFlights, delayType === 'departure' ? 'depCountryName' : 'arrCountryName', delayType, otpDelayMinutes),
            delayTimeByCountry: countValuesByField(filteredFlights, delayType === 'departure' ? 'depDelaysTime' : 'arrDelaysTime', delayType === 'departure' ? 'depCountryName' : 'arrCountryName', 'barlines', otpDelayMinutes),
            countryDelayTimeAffectedByPreviousLeg: countValuesByFields(filteredFlights, 'depDelaysTime', 'preArrDelaysTime', 'depCountryName', otpDelayMinutes),
            paxByCountry: countPaxAffectedByDelay(filteredFlights, delayType === 'departure' ? 'depCountryName' : 'arrCountryName', delayType, 'barlines', otpDelayMinutes),

            otpByFlightNo: countOTPByField(filteredFlights, 'flightNo', delayType, 'barlines', otpDelayMinutes),
            multiOTPByFlightNo: countOTPAffectedByArrDelay(filteredFlights, 'flightNo', delayType, 'barlines', otpDelayMinutes),
            propagationIndexByFlightNo: countDelayPropagationMetrics(filteredFlights, 'flightNo', otpDelayMinutes),
            otpByStackedFlightNo: countOTPByFieldStackedBarChart(filteredFlights, 'flightNo', delayType, otpDelayMinutes),
            delayTimeByFlightNo: countValuesByField(filteredFlights, delayType === 'departure' ? 'depDelaysTime' : 'arrDelaysTime', 'flightNo', 'barlines', otpDelayMinutes),
            flightNoDelayTimeAffectedByPreviousLeg: countValuesByFields(filteredFlights, 'depDelaysTime', 'preArrDelaysTime', 'flightNo', otpDelayMinutes),
            paxByFlightNo: countPaxAffectedByDelay(filteredFlights, 'flightNo', delayType, 'barlines', otpDelayMinutes),

            otpByDelayCode: countDepOTPByDelayCode(filteredFlights, delayType),

            otpByETDRange: calculateOTPByETDRange(filteredFlights, timeMode, delayType, otpDelayMinutes),
            delayTimeByETDRange: calculateDelaysTimeByETDRange(filteredFlights, timeMode, delayType, otpDelayMinutes),

            otpByWeekday: calculateOTPByWeekday(filteredFlights, timeMode, delayType, otpDelayMinutes),
            delayTimeByWeekday: calculateDelaysTimeByWeekday(filteredFlights, timeMode, delayType, otpDelayMinutes),

            heatmapByDelayCode: countHeatmapByDelayCode(filteredFlights, timeMode, delayType),

            tableauOtpByDate: countOTPBy2Fields(filteredFlights, flightDate, null, delayType, otpDelayMinutes ),  
            tableauOtpByDateDep: countOTPBy2Fields(filteredFlights, flightDate, 'dep', delayType, otpDelayMinutes),  
            tableauOtpByDateSector: countOTPBy2Fields(filteredFlights, flightDate, 'sector', delayType, otpDelayMinutes),
            tableauOtpByDateSectorType: countOTPBy2Fields(filteredFlights, flightDate, 'sectorType', delayType, otpDelayMinutes),
            tableauOtpByDateFlightType: countOTPBy2Fields(filteredFlights, flightDate, 'flightType', delayType, otpDelayMinutes),
            tableauOtpByDateFlightReg: countOTPBy2Fields(filteredFlights, flightDate, 'flightReg', delayType, otpDelayMinutes),
            tableauOtpByDateACType: countOTPBy2Fields(filteredFlights, flightDate, 'acType', delayType, otpDelayMinutes),
            tableauOtpByDateCountry: countOTPBy2Fields(filteredFlights, flightDate, 'depCountryName', delayType, otpDelayMinutes),
            tableauOtpByDateFlightNo: countOTPBy2Fields(filteredFlights, flightDate, 'flightNo', delayType, otpDelayMinutes),

            tableauOtpByDep: countOTPBy2Fields(filteredFlights, 'dep', null, delayType, otpDelayMinutes),
            tableauOtpBySector: countOTPBy2Fields(filteredFlights, 'sector', null, delayType, otpDelayMinutes),
            tableauOtpBySectorType: countOTPBy2Fields(filteredFlights, 'sectorType', null, delayType, otpDelayMinutes),
            tableauOtpByFlightType: countOTPBy2Fields(filteredFlights, 'flightType', null, delayType, otpDelayMinutes),
            
            tableauOtpByFlightReg: countOTPBy2Fields(filteredFlights, 'flightReg', null, delayType, otpDelayMinutes),
            tableauOtpByFlightRegDep: countOTPBy2Fields(filteredFlights, 'flightReg', 'dep', delayType, otpDelayMinutes),
            tableauOtpByFlightRegSector: countOTPBy2Fields(filteredFlights, 'flightReg', 'sector', delayType, otpDelayMinutes),
            tableauOtpByFlightRegSectorType: countOTPBy2Fields(filteredFlights, 'flightReg', 'sectorType', delayType, otpDelayMinutes),
            tableauOtpByFlightRegFlightType: countOTPBy2Fields(filteredFlights, 'flightReg', 'flightType', delayType, otpDelayMinutes),
            tableauOtpByFlightRegCountry: countOTPBy2Fields(filteredFlights, 'flightReg', 'depCountryName', delayType, otpDelayMinutes),
            
            tableauOtpByACType: countOTPBy2Fields(filteredFlights, 'acType', null, delayType, otpDelayMinutes),
            tableauOtpByACTypeDep: countOTPBy2Fields(filteredFlights, 'acType', 'dep', delayType, otpDelayMinutes),
            tableauOtpByACTypeSector: countOTPBy2Fields(filteredFlights, 'acType', 'sector', delayType, otpDelayMinutes),
            tableauOtpByACTypeSectorType: countOTPBy2Fields(filteredFlights, 'acType', 'sectorType', delayType, otpDelayMinutes), 
            tableauOtpByACTypeFlightType: countOTPBy2Fields(filteredFlights, 'acType', 'flightType', delayType, otpDelayMinutes), 
            tableauOtpByACTypeCountry: countOTPBy2Fields(filteredFlights, 'acType', 'depCountryName', delayType, otpDelayMinutes),
            
            tableauOtpByCountry: countOTPBy2Fields(filteredFlights, 'depCountryName', null, delayType, otpDelayMinutes),
            
            tableauOtpByFlightNo: countOTPBy2Fields(filteredFlights, 'flightNo', null, delayType, otpDelayMinutes), 
        };            
        setData(results);  
    }, [timeMode, filteredFlights, delayType, otpDelayMinutes]); 
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        if (!Array.isArray(flights)) {
            return {
                acTypeOptions: [],
                flightRegOptions: [],
                sectorTypeOptions: [],
                departureTypeOptions: [],
                arrivalTypeOptions: [],
            };
        }

        const sorted = (key) =>
            [...new Set(flights.map((f) => f[key]).filter(Boolean))].sort();

        return {
            acTypeOptions: sorted("acType"),
            flightRegOptions: sorted("flightReg"),
            sectorTypeOptions: sorted("sectorType"),
            departureTypeOptions: sorted("departureType"),
            arrivalTypeOptions: sorted("arrivalType"),
        };
    }, [flights]); 
    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleChartClose = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `OTP ${period}`;
    const revisedTitle = `Number of delays ${period}`;
    const modifiedTitle = `OTP affected by previous sector's Arrival Delays ${period}`;
    const propagationTitle = `Propagation Index ${period}`;
    const delayMinutesTitle = `Delay minutes affected by previous sector's Arrival Delays ${period}`;
    const paxTitle = `Pax affected by delays ${period}`;
    const fromDate = startDate && format(parseUTCDate(addDays(startDate, -1)), 'yyyy-MM-dd');
    const toDate = endDate && format(parseUTCDate(endDate), 'yyyy-MM-dd');
    
    const chartMap = {
        chart: {
            otpOverall: [
                { component: <PieChart data={data.otpOverallPie} title={defaultTitle} />, style: {} },
                { component: <SortBarChart data={data.otpOverallBar} title={defaultTitle} bottomLegend='No. of Flights' leftLegend='Count' isShowAvgLine={false} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
                { component: <PieChart data={data.otpOverallAffectedByPreviousLegPie} title={revisedTitle} itemWidthOffSet={30} itemsSpacingOffSet={20} />, style: {} },
                { component: <PieChart data={data.paxOverallPie} title={paxTitle} />, style: {} },
                { component: <SortBarChart data={data.paxOverallBar} title={paxTitle} bottomLegend='No. of Passengers' leftLegend='Count' isShowAvgLine={false} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
            ],
            otpByDate: [
                { component: <SortBarMultiLinesChart data={data.otpByFlightDate} title={defaultTitle} bottomLegend='Date' leftLegend='OTP (%)' digit={1} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.multiOTPByFlightDate} title={modifiedTitle} bottomLegend='Date' leftLegend='Percentage (%)' digit={1} barTranslateXOffSet={-35} lineTranslateXOffSet={-10} itemsSpacingOffSet={70} />, style: {} },
                { component: <SortBarChart data={data.propagationIndexByFlightDate} title={propagationTitle} bottomLegend='Date' leftLegend='depDelayTime / arrDelayTime' />, style: {} },
                { component: <TimeRangeChart data={data.otpTimeRangeByFlightDate} fromDate={fromDate} toDate={toDate} title={defaultTitle} digit={1} itemWidthOffSet={isMobile ? -65 : -60} />, style: {} },
                { component: <SortStackedBarChart data={data.otpByStackedFlightDate} title={defaultTitle} bottomLegend='Date' leftLegend='Flights' digit={1} itemWidthOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeByFlightDate} fieldName='depDelaysTime' title={`Delay minutes ${period}`} bottomLegend='Date' leftLegend='Minutes' digit={0} barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.flightDateDelayTimeAffectedByPreviousLeg} fieldName='depDelaysTime' title={delayMinutesTitle} bottomLegend='Date' leftLegend='Minutes' digit={0} barTranslateXOffSet={-188} lineTranslateXOffSet={-35} itemsSpacingOffSet={55} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByFlightDate} title={paxTitle} bottomLegend='Date' leftLegend='No. of Pax' digit={0} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpByPort: [
                { component: <SortBarMultiLinesChart data={data.otpByPort} title={defaultTitle} bottomLegend='Port' leftLegend='OTP (%)' digit={1} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.multiOTPByPort} title={modifiedTitle} bottomLegend='Port' leftLegend='Percentage (%)' digit={1} barTranslateXOffSet={-35} lineTranslateXOffSet={-10} itemsSpacingOffSet={70} />, style: {} },
                { component: <SortBarChart data={data.propagationIndexByPort} title={propagationTitle} bottomLegend='Port' leftLegend='depDelayTime / arrDelayTime' bottomOffSet={0} bottomLegendOffSet={0} />, style: {} },
                { component: <SortStackedBarChart data={data.otpByStackedPort} title={defaultTitle} bottomLegend='Port' leftLegend='Flights' itemWidthOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeByPort} fieldName='depDelaysTime' title={`Delay minutes ${period}`} bottomLegend='Port' leftLegend='Minutes' digit={0} barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.depDelayTimeAffectedByPreviousLeg} fieldName='depDelaysTime' title={delayMinutesTitle} bottomLegend='Port' leftLegend='Minutes' digit={0} barTranslateXOffSet={-188} lineTranslateXOffSet={-35} itemsSpacingOffSet={55} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByPort} title={paxTitle} bottomLegend='Port' leftLegend='No. of Pax' digit={0} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpBySector: [
                { component: <SortBarMultiLinesChart data={data.otpBySector} title={defaultTitle} bottomLegend='Sector' leftLegend='OTP (%)' digit={1} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.multiOTPBySector} title={modifiedTitle} bottomLegend='Sector' leftLegend='Percentage (%)' digit={1} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={-35} lineTranslateXOffSet={-10} itemsSpacingOffSet={70} />, style: {} },
                { component: <SortBarChart data={data.propagationIndexBySector} title={propagationTitle} bottomLegend='Sector' leftLegend='depDelayTime / arrDelayTime' bottomOffSet={10} bottomLegendOffSet={10} />, style: {} },
                { component: <SortStackedBarChart data={data.otpByStackedSector} title={defaultTitle} bottomLegend='Sector' leftLegend='Flights' bottomOffSet={10} bottomLegendOffSet={10} itemWidthOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeBySector} fieldName='depDelaysTime' title={`Delay minutes ${period}`} bottomLegend='Sector' leftLegend='Minutes' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.sectorDelayTimeAffectedByPreviousLeg} fieldName='depDelaysTime' title={delayMinutesTitle} bottomLegend='Sector' leftLegend='Minutes' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={-188} lineTranslateXOffSet={-35} itemsSpacingOffSet={55} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxBySector} title={paxTitle} bottomLegend='Sector' leftLegend='No. of Pax' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpBySectorType: [
                { component: <SortBarMultiLinesChart data={data.otpBySectorType} title={defaultTitle} bottomLegend='Sector Type' leftLegend='OTP (%)' isHorizontalBottomLegend={true} digit={1} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.multiOTPBySectorType} title={modifiedTitle} bottomLegend='Sector Type' leftLegend='Percentage (%)' isHorizontalBottomLegend={true} digit={1} bottomOffSet={-30} bottomLegendOffSet={-30}  barTranslateXOffSet={-35} lineTranslateXOffSet={-10} itemsSpacingOffSet={70} />, style: {} },
                { component: <SortBarChart data={data.propagationIndexBySectorType} title={propagationTitle} bottomLegend='Sector Type' leftLegend='depDelayTime / arrDelayTime' isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
                { component: <SortStackedBarChart data={data.otpByStackedSectorType} title={defaultTitle} bottomLegend='Sector Type' leftLegend='Flights' isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} itemWidthOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeBySectorType} title={`Delay minutes ${period}`} fieldName='depDelaysTime' bottomLegend='Sector Type' leftLegend='Minutes' digit={0} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.sectorTypeDelayTimeAffectedByPreviousLeg} fieldName='depDelaysTime' title={delayMinutesTitle} bottomLegend='Sector Type' leftLegend='Minutes' digit={0} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={-188} lineTranslateXOffSet={-35} itemsSpacingOffSet={55} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxBySectorType} title={paxTitle} bottomLegend='Sector Type' leftLegend='No. of Pax' digit={0} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpByFlightType: [
                { component: <SortBarMultiLinesChart data={data.otpByFlightType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='OTP (%)' isHorizontalBottomLegend={!isMobile ? true : undefined} digit={1} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.multiOTPByFlightType} title={modifiedTitle} bottomLegend='Flight Type' leftLegend='Percentage (%)' isHorizontalBottomLegend={!isMobile ? true : undefined} digit={1} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={-35} lineTranslateXOffSet={-10} itemsSpacingOffSet={70} />, style: {} },
                { component: <SortBarChart data={data.propagationIndexByFlightType} title={propagationTitle} bottomLegend='Flight Type' leftLegend='depDelayTime / arrDelayTime' isHorizontalBottomLegend={!isMobile ? true : undefined} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} />, style: {} },
                { component: <SortStackedBarChart data={data.otpByStackedFlightType} title={defaultTitle} bottomLegend='Flight Type' leftLegend='Flights' isHorizontalBottomLegend={!isMobile ? true : undefined} digit={0} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} itemWidthOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeByFlightType} title={`Delay minutes ${period}`} fieldName='depDelaysTime' bottomLegend='Flight Type' leftLegend='Minutes' digit={0} isHorizontalBottomLegend={!isMobile ? true : undefined} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.flightTypeDelayTimeAffectedByPreviousLeg} fieldName='depDelaysTime' title={delayMinutesTitle} bottomLegend='Flight Type' leftLegend='Minutes' digit={0} isHorizontalBottomLegend={!isMobile ? true : undefined} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={-188} lineTranslateXOffSet={-35} itemsSpacingOffSet={55} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByFlightType} title={paxTitle} bottomLegend='Flight Type' leftLegend='No. of Pax' isHorizontalBottomLegend={!isMobile ? true : undefined} digit={0} bottomOffSet={!isMobile ? -30 : 50} bottomLegendOffSet={!isMobile ? -30 : 50} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpByFlightReg: [
                { component: <SortBarMultiLinesChart data={data.otpByFlightReg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='OTP (%)' digit={1} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.multiOTPByFlightReg} title={modifiedTitle} bottomLegend='Flight Reg' leftLegend='Percentage (%)' digit={1} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={-35} lineTranslateXOffSet={-10} itemsSpacingOffSet={70} />, style: {} },
                { component: <SortBarChart data={data.propagationIndexByFlightReg} title={propagationTitle} bottomLegend='Flight Reg' leftLegend='depDelayTime / arrDelayTime' bottomOffSet={5} bottomLegendOffSet={5} />, style: {} },
                { component: <SortStackedBarChart data={data.otpByStackedFlightReg} title={defaultTitle} bottomLegend='Flight Reg' leftLegend='Flights' bottomOffSet={5} bottomLegendOffSet={5} itemWidthOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeByFlightReg} fieldName='depDelaysTime' title={`Delay minutes ${period}`} bottomLegend='Flight Reg' leftLegend='Minutes' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.flightRegDelayTimeAffectedByPreviousLeg} fieldName='depDelaysTime' title={delayMinutesTitle} bottomLegend='Flight Reg' leftLegend='Minutes' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={-188} lineTranslateXOffSet={-35} itemsSpacingOffSet={55} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByFlightReg} title={paxTitle} bottomLegend='Flight Reg' leftLegend='No. of Pax' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpByACType: [
                { component: <SortBarMultiLinesChart data={data.otpByACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='OTP (%)' digit={1} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.multiOTPByACType} title={modifiedTitle} bottomLegend='AC Type' leftLegend='Percentage (%)' bottomOffSet={0} bottomLegendOffSet={0} digit={1} barTranslateXOffSet={-35} lineTranslateXOffSet={-10} itemsSpacingOffSet={70} />, style: {} },
                { component: <SortBarChart data={data.propagationIndexByACType} title={propagationTitle} bottomLegend='AC Type' leftLegend='depDelayTime / arrDelayTime' bottomOffSet={0} bottomLegendOffSet={0} />, style: {} },
                { component: <SortStackedBarChart data={data.otpByStackedACType} title={defaultTitle} bottomLegend='AC Type' leftLegend='Flights' itemWidthOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeByACType} title={`Delay minutes ${period}`} fieldName='depDelaysTime' bottomLegend='AC Type' leftLegend='Minutes' digit={0} barTranslateXOffSet={60} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.acTypeDelayTimeAffectedByPreviousLeg} fieldName='depDelaysTime' title={delayMinutesTitle} bottomLegend='AC Type' leftLegend='Minutes' digit={0} bottomOffSet={0} bottomLegendOffSet={0} barTranslateXOffSet={-188} lineTranslateXOffSet={-35} itemsSpacingOffSet={55} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByACType} title={paxTitle} bottomLegend='AC Type' leftLegend='No. of Pax' digit={0} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpByCountry: [
                { component: <SortBarMultiLinesChart data={data.otpByCountry} title={defaultTitle} bottomLegend='Country' leftLegend='OTP (%)' digit={1} bottomOffSet={25} bottomLegendOffSet={25} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.multiOTPByCountry} title={modifiedTitle} bottomLegend='Country' leftLegend='Percentage (%)' digit={1} bottomOffSet={25} bottomLegendOffSet={25} barTranslateXOffSet={-35} lineTranslateXOffSet={-10} itemsSpacingOffSet={70} />, style: {} },
                { component: <SortBarChart data={data.propagationIndexByCountry} title={propagationTitle} bottomLegend='Country' leftLegend='depDelayTime / arrDelayTime' bottomOffSet={25} bottomLegendOffSet={25} />, style: {} },
                { component: <SortStackedBarChart data={data.otpByStackedCountry} title={defaultTitle} bottomLegend='Country' leftLegend='Flights' bottomOffSet={25} bottomLegendOffSet={25} itemWidthOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeByCountry} title={`Delay minutes ${period}`} fieldName='depDelaysTime' bottomLegend='Country' leftLegend='Minutes' digit={0} bottomOffSet={25} bottomLegendOffSet={25} barTranslateXOffSet={60} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.countryDelayTimeAffectedByPreviousLeg} fieldName='depDelaysTime' title={delayMinutesTitle} bottomLegend='Country' leftLegend='Minutes' digit={0} bottomOffSet={25} bottomLegendOffSet={25} barTranslateXOffSet={-188} lineTranslateXOffSet={-35} itemsSpacingOffSet={55} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByCountry} title={paxTitle} bottomLegend='Country' leftLegend='No. of Pax' digit={0} bottomOffSet={25} bottomLegendOffSet={25} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpByFlightNo: [
                { component: <SortBarMultiLinesChart data={data.otpByFlightNo} title={defaultTitle} bottomLegend='Flight No' leftLegend='OTP (%)' digit={1} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.multiOTPByFlightNo} title={modifiedTitle} bottomLegend='Flight No' leftLegend='Percentage (%)' digit={1} bottomOffSet={5} bottomLegendOffSet={5}  barTranslateXOffSet={-35} lineTranslateXOffSet={-10} itemsSpacingOffSet={70} />, style: {} },
                { component: <SortBarChart data={data.propagationIndexByFlightNo} title={propagationTitle} bottomLegend='Flight No' leftLegend='depDelayTime / arrDelayTime' bottomOffSet={5} bottomLegendOffSet={5} />, style: {} },
                { component: <SortStackedBarChart data={data.otpByStackedFlightNo} title={defaultTitle} bottomLegend='Flight No' leftLegend='Flights' bottomOffSet={5} bottomLegendOffSet={5} itemWidthOffSet={-15} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeByFlightNo} fieldName='depDelaysTime' title={`Delay minutes ${period}`} bottomLegend='Flight No' leftLegend='Minutes' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.flightNoDelayTimeAffectedByPreviousLeg} fieldName='depDelaysTime' title={delayMinutesTitle} bottomLegend='Flight No' leftLegend='Minutes' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={-188} lineTranslateXOffSet={-35} itemsSpacingOffSet={55} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.paxByFlightNo} title={paxTitle} bottomLegend='Flight No' leftLegend='No. of Pax' digit={0} bottomOffSet={5} bottomLegendOffSet={5} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpByDelayCode: [
                { component: <SortBarChart data={data.otpByDelayCode} title={`No. of Delays ${period}`} bottomLegend='Delay Code' leftLegend='Count' isHorizontalBottomLegend={true} digit={1} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
            ],
            otpByETDRange: [
                { component: <SortBarMultiLinesChart data={data.otpByETDRange} title={defaultTitle} bottomLegend='Oclock' leftLegend='OTP (%)' digit={1} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeByETDRange} fieldName='depDelaysTime' title={`Delay minutes ${period}`} bottomLegend='Oclock' leftLegend='Minutes' digit={0} barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
            ],
            otpByWeekday: [
                { component: <SortBarMultiLinesChart data={data.otpByWeekday} title={defaultTitle} bottomLegend='Weekday' leftLegend='OTP (%)' digit={1} barTranslateXOffSet={110} lineTranslateXOffSet={-10} />, style: {} },
                { component: <SortBarMultiLinesChart data={data.delayTimeByWeekday} fieldName='depDelaysTime' title={`Delay minutes ${period}`} bottomLegend='Weekday' leftLegend='Minutes' digit={0} barTranslateXOffSet={65} lineTranslateXOffSet={-10} />, style: {} },
            ],
            heatmapByDelayCode: [
                { component: <HeatMap data={data.heatmapByDelayCode} title={`Delays Heat Map ${period}`} />, style: {pt: 3} },
            ],
        },
        tableau: {
            otpByDate: [
                { component: <OtpTableau data={data.tableauOtpByDate} /> },
            ],
            otpByDateDep: [
                { component: <OtpTableau data={data.tableauOtpByDateDep} /> },
            ],
            otpByDateSector: [
                { component: <OtpTableau data={data.tableauOtpByDateSector} /> },
            ],
            otpByDateSectorType: [
                { component: <OtpTableau data={data.tableauOtpByDateSectorType} /> },
            ],
            otpByDateFlightType: [
                { component: <OtpTableau data={data.tableauOtpByDateFlightType} /> },
            ],
            otpByDateFlightReg: [
                { component: <OtpTableau data={data.tableauOtpByDateFlightReg} /> },
            ],
            otpByDateACType: [
                { component: <OtpTableau data={data.tableauOtpByDateACType} /> },
            ],
            otpByDateCountry: [
                { component: <OtpTableau data={data.tableauOtpByDateCountry} /> },
            ],
            otpByDateFlightNo: [
                { component: <OtpTableau data={data.tableauOtpByDateFlightNo} /> },
            ],
            otpByDep: [
                { component: <OtpTableau data={data.tableauOtpByDep} /> },
            ],
            otpBySector: [
                { component: <OtpTableau data={data.tableauOtpBySector} /> },
            ],
            otpBySectorType: [
                { component: <OtpTableau data={data.tableauOtpBySectorType} /> },
            ],
            otpByFlightType: [
                { component: <OtpTableau data={data.tableauOtpByFlightType} /> },
            ],
            otpByCountry: [
                { component: <OtpTableau data={data.tableauOtpByCountry} /> },
            ],
            otpByFlightReg: [
                { component: <OtpTableau data={data.tableauOtpByFlightReg} /> },
            ],
            otpByFlightRegDep: [
                { component: <OtpTableau data={data.tableauOtpByFlightRegDep} /> },
            ],
            otpByFlightRegSector: [
                { component: <OtpTableau data={data.tableauOtpByFlightRegSector} /> },
            ],
            otpByFlightRegSectorType: [
                { component: <OtpTableau data={data.tableauOtpByFlightRegSectorType} /> },
            ],
            otpByFlightRegFlightType: [
                { component: <OtpTableau data={data.tableauOtpByFlightRegFlightType} /> },
            ],
            otpByFlightRegCountry: [
                { component: <OtpTableau data={data.tableauOtpByFlightRegCountry} /> },
            ],
            otpByACType: [
                { component: <OtpTableau data={data.tableauOtpByACType} /> },
            ],
            otpByACTypeDep: [
                { component: <OtpTableau data={data.tableauOtpByACTypeDep} /> },
            ],
            otpByACTypeSector: [
                { component: <OtpTableau data={data.tableauOtpByACTypeSector} /> },
            ],
            otpByACTypeSectorType: [
                { component: <OtpTableau data={data.tableauOtpByACTypeSectorType} /> },
            ],
            otpByACTypeFlightType: [
                { component: <OtpTableau data={data.tableauOtpByACTypeFlightType} /> },
            ],
            otpByACTypeCountry: [
                { component: <OtpTableau data={data.tableauOtpByACTypeCountry} /> },
            ],
            otpByFlightNo: [
                { component: <OtpTableau data={data.tableauOtpByFlightNo} /> },
            ],
        }  
    };
    
    const renderCharts = (charts, customStyles) => (        
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}    
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width="100%"
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header 
                    title="OTP" 
                    subtitle={`${loading ? 'Loading...' : displayType === 'chart' ? 'List of Charts' : 'List of Tableaus'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        {!isMobile && (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Tooltip title="Minimum delay minutes" arrow>
                                    <span>
                                        <NumberControl
                                            min={0}
                                            max={99999}
                                            value={otpDelayMinutes}
                                            onChange={(value) => {
                                                setOtpDelayMinutes(value); 
                                            }} 
                                            textFieldWidth={45}
                                        />
                                    </span>
                                </Tooltip>    
                                <FormControl
                                    size='small'
                                    sx={{  width: { xs: '6.8rem', sm: '8rem' } }}
                                >
                                    <InputLabel>Delay Type</InputLabel>
                                    <Select
                                        label="Delay Type"
                                        value={delayType}
                                        onChange={(e) => setDelayType(e.target.value)}                                
                                    >
                                        <MenuItem value="departure">Departure</MenuItem>
                                        <MenuItem value="arrival">Arrival</MenuItem>
                                    </Select>
                                </FormControl>                                  
                            </Box>              
                        )} 

                        <FormControl 
                            size="small" 
                            sx={{ width: { xs: "6rem", sm: "8rem" } }}
                        >
                            <InputLabel>Display</InputLabel>
                            <Select
                                value={displayType}
                                label="Display"
                                onChange={(e) => setDisplayType(e.target.value)}
                            >
                                {displayOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl 
                            size="small" 
                            sx={{ width: { xs: "7rem", sm: "12rem" } }}
                        >
                            <InputLabel>View</InputLabel>
                            <Select
                                value={safeView} 
                                label="View"
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions[displayType].map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <FilterButton
                            filters={filters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}
            </FlexBetween>                        
            {loading ? (
                <Box sx={{ ...styles }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles }}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >                    
                    {displayType in chartMap && view in chartMap[displayType] && 
                        renderCharts(chartMap[displayType][view], {  minHeight: LAYOUT_HEIGHT })
                    }
                    
                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}

            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleChartClose}
                    chartComponent={selectedChart}
                />
            )}

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default OTPAnalytics;
